import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Text } from "@zorni/components-lib";
import {
  MAX_UPLOAD_FILE_SIZE_MB,
  STANDARD_UPLOAD_EXTENSIONS,
  getAcceptAttribute,
} from "../../../common/upload/uploadFileRules";
import { useUploadFileGuard } from "../../../common/upload/useUploadFileGuard";
import UploadFileError from "../../../common/upload/UploadFileError";
import styles from "./OrganismeLogoUpload.module.css";

/**
 * Zone de dépôt du logo de l'organisme (aperçu rond + bouton "Parcourir",
 * façon maquette). Ne fait *pas* l'upload lui-même : il remonte le fichier
 * choisi au parent via onFileSelected, qui l'ajoute à son fileList et
 * l'envoie réellement à /upload au moment de l'enregistrement (même
 * pattern déjà utilisé pour le reste de l'app, cf. useUpload).
 *
 * Remplace OrganismePhoto/UploadOrganismeModal : ces derniers ne
 * déclenchaient jamais le vrai upload (ils se contentaient de fixer
 * l'aperçu base64 comme "picture" finale) — aucun équivalent générique
 * dans zorni-components-lib pour ce widget avatar-avec-caméra, d'où ce
 * petit composant dédié.
 */
const OrganismeLogoUpload = ({ previewUrl, onFileSelected }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const { guardOnUpload, fileError } = useUploadFileGuard(STANDARD_UPLOAD_EXTENSIONS);

  // Sélection (clic ou dépôt) : un fichier refusé n'atteint jamais le parent.
  const handleFile = (file) => {
    if (!file) return;
    guardOnUpload((accepted) => {
      if (!accepted.length) return;
      setFileName(file.name);
      onFileSelected(file);
    })([file]);
  };

  const handleDrag = (dragging) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(dragging);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    handleFile(event.dataTransfer?.files?.[0]);
  };

  const openFileDialog = () => inputRef.current?.click();

  return (
    <label
      className={[styles.upload, isDragging && styles.isDrag, fileName && styles.hasFile]
        .filter(Boolean)
        .join(" ")}
      onDragEnter={handleDrag(true)}
      onDragOver={handleDrag(true)}
      onDragLeave={handleDrag(false)}
      onDrop={handleDrop}
    >
      <span className={styles.thumbWrap}>
        {previewUrl ? (
          <img className={styles.thumb} src={previewUrl} alt="" />
        ) : (
          <span className={styles.thumbFallback}>
            <Icon name="iconBuildings" size="md" />
          </span>
        )}
        <span className={styles.cam}>
          <Icon name="iconCamera" size="xs" />
        </span>
      </span>
      <span className={styles.info}>
        <Text as="strong" weight="semibold">
          {t("organism.upload.title", "Glissez-déposez votre image")}
        </Text>
        <Text as="span" size="sm" color="muted">
          {t(
            "organism.upload.subtitle",
            "ou cliquez pour parcourir · JPEG, JPG, PNG, PDF · {{maxSizeMb}} Mo max",
            { maxSizeMb: MAX_UPLOAD_FILE_SIZE_MB }
          )}
        </Text>
        {fileName && (
          <span className={styles.fileChip}>
            <Icon name="iconCheck" size="xs" />
            {fileName}
          </span>
        )}
        <UploadFileError message={fileError} />
      </span>
      <span className={styles.cta} onClick={openFileDialog}>
        <Icon name="iconUpload" size="sm" />
        {t("organism.upload.cta", "Parcourir")}
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={getAcceptAttribute(STANDARD_UPLOAD_EXTENSIONS)}
        className={styles.hiddenInput}
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
    </label>
  );
};

export default OrganismeLogoUpload;
